import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuinielaStore } from '../../store/quinielaStore'
import { useAuthStore } from '../../store/authStore'
import { Calendar, DollarSign, Users, Trophy, FileText, Loader, AlertCircle, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { getRounds, getFixturesByRound, LEAGUES, formatFixture } from '../../services/apiFootball'

export default function CreateQuiniela() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { createQuiniela, loading } = useQuinielaStore()
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    league: 'liga_mx',
    season: '2026',
    jornada: '',
    roundName: '', // Nombre completo de la jornada (ej: "Regular Season - 1")
    entryFee: '',
    startDate: '',
    endDate: '',
    maxLimit: '',
    matchIds: []
  })

  const [errors, setErrors] = useState({})
  const [rounds, setRounds] = useState([])
  const [availableFixtures, setAvailableFixtures] = useState([])
  const [selectedFixtures, setSelectedFixtures] = useState([])
  const [loadingFixtures, setLoadingFixtures] = useState(false)

  // Cargar jornadas al montar el componente y cuando cambie la temporada
  useEffect(() => {
    loadRounds()
  }, [formData.season])

  const loadRounds = async () => {
    try {
      let leagueId = LEAGUES.LIGA_MX
      if (formData.league === 'mundial') leagueId = LEAGUES.MUNDIAL
      if (formData.league === 'champions_league') leagueId = LEAGUES.CHAMPIONS_LEAGUE
      const season = parseInt(formData.season) || 2026
      console.log('Loading rounds for season:', season)
      // Diagnóstico: probar temporadas cercanas
      console.log('🔍 Diagnostic: Testing seasons around', season)
      for (let testSeason of [season, season - 1, season + 1]) {
        const testRounds = await getRounds(leagueId, testSeason)
        console.log(`Season ${testSeason}:`, testRounds.length, 'rounds found')
        if (testRounds.length > 0) {
          console.log('✅ Found rounds in season:', testSeason)
          console.log('Sample rounds:', testRounds.slice(0, 3))
        }
      }
      const roundsData = await getRounds(leagueId, season)
      console.log('Rounds loaded:', roundsData)
      // Filtrar solo jornadas futuras
      const now = new Date()
      const futureRounds = []
      for (const round of roundsData) {
        const fixtures = await getFixturesByRound(leagueId, season, round)
        if (fixtures.some(f => new Date(f.fixture.date) > now)) {
          futureRounds.push(round)
        }
      }
      setRounds(futureRounds)
      if (futureRounds.length === 0) {
        toast.error('No hay jornadas futuras disponibles para esta temporada')
      }
    } catch (error) {
      console.error('Error loading rounds:', error)
      toast.error('Error al cargar las jornadas: ' + error.message)
    }
  }

  const loadFixturesByRound = async (round) => {
    if (!round) return
    
    setLoadingFixtures(true)
    try {
      let leagueId = LEAGUES.LIGA_MX
      if (formData.league === 'mundial') leagueId = LEAGUES.MUNDIAL
      if (formData.league === 'champions_league') leagueId = LEAGUES.CHAMPIONS_LEAGUE
      const season = parseInt(formData.season) || 2026
      console.log('Loading fixtures for round:', round, 'season:', season)
      const fixtures = await getFixturesByRound(leagueId, season, round)
      
      const formattedFixtures = fixtures.map(formatFixture)
      setAvailableFixtures(formattedFixtures)
      
      // Auto-calcular fechas de inicio y fin
      if (formattedFixtures.length > 0) {
        const dates = formattedFixtures.map(f => f.date)
        const minDate = new Date(Math.min(...dates))
        const maxDate = new Date(Math.max(...dates))
        
        setFormData(prev => ({
          ...prev,
          startDate: minDate.toISOString().slice(0, 16),
          endDate: maxDate.toISOString().slice(0, 16)
        }))
      }
      
      toast.success(`${formattedFixtures.length} partidos cargados`)
    } catch (error) {
      console.error('Error loading fixtures:', error)
      toast.error('Error al cargar los partidos')
    } finally {
      setLoadingFixtures(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Si cambia la temporada, limpiar jornadas y partidos
    if (name === 'season') {
      setRounds([])
      setAvailableFixtures([])
      setSelectedFixtures([])
      setFormData(prev => ({
        ...prev,
        jornada: '',
        roundName: '',
        matchIds: [],
        startDate: '',
        endDate: ''
      }))
    }
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleRoundChange = (e) => {
    const roundName = e.target.value
    handleChange(e)
    
    if (roundName) {
      loadFixturesByRound(roundName)
    }
  }

  const toggleFixture = (fixture) => {
    const fixtureId = fixture.id.toString()
    const isSelected = selectedFixtures.some(f => f.id === fixture.id)
    
    if (isSelected) {
      setSelectedFixtures(prev => prev.filter(f => f.id !== fixture.id))
      setFormData(prev => ({
        ...prev,
        matchIds: prev.matchIds.filter(id => id !== fixtureId)
      }))
    } else {
      setSelectedFixtures(prev => [...prev, fixture])
      setFormData(prev => ({
        ...prev,
        matchIds: [...prev.matchIds, fixtureId]
      }))
    }
  }

  const selectAllFixtures = () => {
    setSelectedFixtures([...availableFixtures])
    setFormData(prev => ({
      ...prev,
      matchIds: availableFixtures.map(f => f.id.toString())
    }))
    toast.success('Todos los partidos seleccionados')
  }

  const clearAllFixtures = () => {
    setSelectedFixtures([])
    setFormData(prev => ({
      ...prev,
      matchIds: []
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    }

    if (!formData.jornada || formData.jornada < 1) {
      newErrors.jornada = 'La jornada debe ser mayor a 0'
    }

    if (!formData.entryFee || parseFloat(formData.entryFee) < 50) {
      newErrors.entryFee = 'El costo mínimo es de $50 pesos'
    }

    if (!formData.startDate) {
      newErrors.startDate = 'La fecha de inicio es requerida'
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La fecha de fin es requerida'
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate)
      const end = new Date(formData.endDate)
      
      if (end <= start) {
        newErrors.endDate = 'La fecha de fin debe ser posterior a la de inicio'
      }

      // Validar que la fecha de inicio sea futura
      const now = new Date()
      if (start <= now) {
        newErrors.startDate = 'La fecha de inicio debe ser futura'
      }
    }

    if (formData.matchIds.length === 0) {
      newErrors.matchIds = 'Debes agregar al menos un partido'
    }

    if (formData.maxLimit && parseInt(formData.maxLimit) < 2) {
      newErrors.maxLimit = 'El límite debe ser al menos 2 participantes'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Por favor corrige los errores del formulario')
      return
    }

    const result = await createQuiniela({
      ...formData,
      matches: selectedFixtures, // Pasar los partidos completos, no solo IDs
      createdBy: user.uid
    })

    if (result.success) {
      toast.success('Quiniela creada exitosamente')
      navigate('/operator')
    }
  }

  // Auto-generar nombre basado en jornada
  const handleJornadaChange = (e) => {
    const roundName = e.target.value
    
    // Extraer número de jornada del formato "Regular Season - 15"
    const jornadaMatch = roundName.match(/\d+$/)
    const jornadaNum = jornadaMatch ? jornadaMatch[0] : ''
    
    setFormData(prev => ({
      ...prev,
      jornada: jornadaNum,
      roundName: roundName, // Guardar nombre completo
      name: `Jornada ${jornadaNum} - Liga MX ${prev.season.replace('-', ' ').toUpperCase()}`
    }))
    
    if (errors.jornada) {
      setErrors(prev => ({ ...prev, jornada: '' }))
    }
    
    // Cargar partidos de esta jornada
    loadFixturesByRound(roundName)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#D4AF37] mb-2">Crear Nueva Quiniela</h1>
        <p className="text-[#BDBDBD]">Configura los detalles de la quiniela para esta jornada</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información Básica */}
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#F8F8F8]">
            <FileText size={20} className="text-[#22C55E]" />
            Información Básica
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Nombre de la Quiniela *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Ej: Jornada 15 - Liga MX Apertura 2026"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.name}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Descripción
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="input"
                placeholder="Descripción opcional de la quiniela"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Liga *
              </label>
              <select
                name="league"
                value={formData.league}
                onChange={handleChange}
                className="input"
              >
                <option value="liga_mx">Liga MX</option>
                <option value="mundial">Mundial</option>
                <option value="champions_league">Champions League</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Temporada *
              </label>
              <select
                name="season"
                value={formData.season}
                onChange={handleChange}
                className="input"
              >
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
                <option value="2022">2022</option>
              </select>
              <p className="mt-1 text-xs text-[#BDBDBD]">
                Plan de paga: acceso a todas las temporadas
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Jornada *
              </label>
              <select
                name="roundName"
                value={formData.roundName || ''}
                onChange={handleJornadaChange}
                className={`input ${errors.jornada ? 'border-red-500' : ''}`}
                disabled={rounds.length === 0}
              >
                <option value="">Selecciona una jornada</option>
                {rounds.map((round, index) => (
                  <option key={index} value={round}>
                    {round}
                  </option>
                ))}
              </select>
              {errors.jornada && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.jornada}
                </p>
              )}
              {rounds.length === 0 && (
                <p className="mt-1 text-xs text-[#BDBDBD]">
                  Cargando jornadas disponibles...
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Costo de Entrada * (MXN)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#BDBDBD]" size={20} />
                <input
                  type="number"
                  name="entryFee"
                  value={formData.entryFee}
                  onChange={handleChange}
                  min="50"
                  step="10"
                  className={`pl-10 input ${errors.entryFee ? 'border-red-500' : ''}`}
                  placeholder="100"
                />
              </div>
              {errors.entryFee && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.entryFee}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Fechas */}
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#F8F8F8]">
            <Calendar size={20} className="text-[#22C55E]" />
            Fechas
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Fecha y Hora del Primer Partido *
              </label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className={`input ${errors.startDate ? 'border-red-500' : ''}`}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.startDate}
                </p>
              )}
              <p className="mt-1 text-xs text-[#BDBDBD]">
                El registro se cierra 1 hora antes
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
                Fecha y Hora del Último Partido *
              </label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className={`input ${errors.endDate ? 'border-red-500' : ''}`}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                  <AlertCircle size={14} /> {errors.endDate}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Partidos */}
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-[#F8F8F8]">
              <Trophy size={20} className="text-[#D4AF37]" />
              Partidos de la Jornada
            </h2>
            {availableFixtures.length > 0 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllFixtures}
                  className="text-sm text-[#22C55E] hover:underline"
                  disabled={selectedFixtures.length === availableFixtures.length}
                >
                  Seleccionar todos
                </button>
                {selectedFixtures.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAllFixtures}
                    className="text-sm text-[#B11217] hover:underline"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}
          </div>
          
          {loadingFixtures && (
            <div className="flex justify-center items-center py-12">
              <Loader className="animate-spin text-[#22C55E]" size={40} />
            </div>
          )}

          {!loadingFixtures && availableFixtures.length === 0 && (
            <div className="text-center py-12 bg-[#1A1A1A] rounded-lg border border-[#0F3D2E]/30">
              <Trophy className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
              <h3 className="text-lg font-semibold text-[#F8F8F8] mb-2">
                Selecciona una jornada
              </h3>
              <p className="text-[#BDBDBD]">
                Los partidos se cargarán automáticamente desde API Football
              </p>
            </div>
          )}

          {!loadingFixtures && availableFixtures.length > 0 && (
            <>
              <div className="space-y-3 mb-4">
                {availableFixtures.map((fixture) => {
                  const isSelected = selectedFixtures.some(f => f.id === fixture.id)
                  const fixtureDate = new Date(fixture.date)
                  
                  return (
                    <div
                      key={fixture.id}
                      onClick={() => toggleFixture(fixture)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-[#0F3D2E] bg-[#0F3D2E]/10'
                          : 'border-[#0F3D2E]/20 hover:border-[#0F3D2E]/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-2 flex-1">
                              {fixture.teams.home.logo && (
                                <img 
                                  src={fixture.teams.home.logo} 
                                  alt={fixture.teams.home.name}
                                  className="w-6 h-6 object-contain"
                                />
                              )}
                              <span className="font-medium text-[#F8F8F8]">
                                {fixture.teams.home.name}
                              </span>
                            </div>
                            <span className="text-[#BDBDBD] font-bold">VS</span>
                            <div className="flex items-center gap-2 flex-1 justify-end">
                              <span className="font-medium text-[#F8F8F8]">
                                {fixture.teams.away.name}
                              </span>
                              {fixture.teams.away.logo && (
                                <img 
                                  src={fixture.teams.away.logo} 
                                  alt={fixture.teams.away.name}
                                  className="w-6 h-6 object-contain"
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-[#BDBDBD]">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {fixtureDate.toLocaleDateString('es-MX', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {fixture.venue && (
                              <span className="text-[#BDBDBD]">• {fixture.venue}</span>
                            )}
                          </div>
                        </div>
                        <div className={`ml-4 w-6 h-6 rounded border-2 flex items-center justify-center ${
                          isSelected
                            ? 'bg-[#0F3D2E] border-[#0F3D2E]'
                            : 'border-[#BDBDBD]'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="bg-[#0F3D2E]/10 border border-[#0F3D2E]/30 rounded-lg p-4">
                <p className="text-sm text-[#F8F8F8]">
                  <strong>{selectedFixtures.length}</strong> de <strong>{availableFixtures.length}</strong> partidos seleccionados
                </p>
              </div>
            </>
          )}

          {errors.matchIds && (
            <p className="mt-2 text-sm text-[#B11217] flex items-center gap-1">
              <AlertCircle size={14} /> {errors.matchIds}
            </p>
          )}
        </div>

        {/* Configuración Adicional */}
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#F8F8F8]">
            <Users size={20} className="text-[#22C55E]" />
            Límites y Restricciones
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-[#F8F8F8] mb-1">
              Límite Máximo de Participantes (opcional)
            </label>
            <input
              type="number"
              name="maxLimit"
              value={formData.maxLimit}
              onChange={handleChange}
              min="2"
              className={`input ${errors.maxLimit ? 'border-red-500' : ''}`}
              placeholder="Dejar vacío para sin límite"
            />
            {errors.maxLimit && (
              <p className="mt-1 text-sm text-[#B11217] flex items-center gap-1">
                <AlertCircle size={14} /> {errors.maxLimit}
              </p>
            )}
            <p className="mt-1 text-xs text-[#BDBDBD]">
              Si no se especifica, no habrá límite de participantes
            </p>
          </div>
        </div>

        {/* Resumen de Premios */}
        {formData.entryFee && (
          <div className="bg-[#222222] p-6 rounded-lg border-2 border-[#D4AF37]">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#F8F8F8]">
              <Trophy size={20} className="text-[#D4AF37]" />
              Distribución de Premios (estimado con 10 participantes)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#0F3D2E]">
                <p className="text-sm text-[#BDBDBD] mb-1">Bolsa Total</p>
                <p className="text-2xl font-bold text-[#F8F8F8]">
                  ${(parseFloat(formData.entryFee) * 10).toFixed(2)}
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#D4AF37]">
                <p className="text-sm text-[#BDBDBD] mb-1">1er Lugar (80%)</p>
                <p className="text-2xl font-bold text-[#D4AF37]">
                  ${(parseFloat(formData.entryFee) * 10 * 0.9 * 0.8).toFixed(2)}
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#BDBDBD]">
                <p className="text-sm text-[#BDBDBD] mb-1">2do Lugar (15%)</p>
                <p className="text-2xl font-bold text-[#BDBDBD]">
                  ${(parseFloat(formData.entryFee) * 10 * 0.9 * 0.15).toFixed(2)}
                </p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#CD7F32]">
                <p className="text-sm text-[#BDBDBD] mb-1">3er Lugar (5%)</p>
                <p className="text-2xl font-bold text-[#CD7F32]">
                  ${(parseFloat(formData.entryFee) * 10 * 0.9 * 0.05).toFixed(2)}
                </p>
              </div>
            </div>
            <p className="text-xs text-[#BDBDBD] mt-4">
              * La comisión del 10% se deduce antes de distribuir premios
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/operator')}
            className="btn bg-[#222222] text-[#F8F8F8] border border-[#BDBDBD] hover:bg-[#2A2A2A] flex-1"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn bg-[#0F3D2E] text-[#F8F8F8] hover:bg-[#0A2D1F] flex-1 flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Creando...
              </>
            ) : (
              'Crear Quiniela'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
